import { ReactNode, useEffect, lazy, Suspense } from 'react'
import {
  VacancyMeetingMode,
  InterviewStatus
} from 'api/endpoints/openapi-generated.schemas'
import { setContext } from '@sentry/browser'
import i18n from 'i18next'
import { InterviewRouterRichProps } from './interview-ui.types'
import { LoadingPage } from './pages/loading'
import { callDialogService } from './pages/call/call-dialog.service'
import { useUnit } from 'effector-react'
import { callWsStateService } from './pages/call/call-ws-state.service'
import { meetingModeSwitcherService } from 'features/meeting-mode-switcher.service'

const CallOnlyChatDynamic = lazy(() =>
  import('./pages/call-chat').then((x) => ({ default: x.CallChatUi }))
)
const CallWith11LabsDynamic = lazy(() =>
  import('./pages/call-11labs').then((x) => ({
    default: x.CallWith11Labs
  }))
)
const CallPresentationDynamic = lazy(() =>
  import('./pages/call-presentation').then((x) => ({
    default: x.CallPresentationUi
  }))
)
const CallWithHeygenDynamic = lazy(() =>
  import('./pages/call-heygen').then((x) => ({
    default: x.CallWithHeygenUi
  }))
)

export function InterviewPermissionsAndCall(
  props: InterviewRouterRichProps
): ReactNode {
  const { vacancy, interview, initialMeetingMode } = props

  const callEndReceived = useUnit(callWsStateService.$callEndReceived)
  const currentMeetingMode = useUnit(
    meetingModeSwitcherService.$currentMeetingMode
  )

  const meetingMode = currentMeetingMode ?? initialMeetingMode

  const isCallFinished =
    callEndReceived ||
    [
      InterviewStatus.cancelled,
      InterviewStatus.failed,
      InterviewStatus.completed,
      InterviewStatus.incomplete
    ].some((x) => x === interview.status)

  useEffect(() => {
    setContext('interview', {
      vacancyId: vacancy.id,
      vacancyTitle: vacancy.title,
      companyId: vacancy.company.id,
      companyName: vacancy.company.name,
      lang: i18n.language
    })
  }, [vacancy, interview])

  if (initialMeetingMode === 'presentation_video_to_voice_transition') {
    return (
      <Suspense
        fallback={
          <LoadingPage tag="Suspense fallback (LoadingPage): CallPresentationDynamic" />
        }
      >
        <CallPresentationDynamic {...props} isCallFinished={isCallFinished} />
      </Suspense>
    )
  }

  if (meetingMode === VacancyMeetingMode.chat_only) {
    callDialogService.strategy = 'chat'

    return (
      <Suspense
        fallback={
          <LoadingPage tag="Suspense fallback (LoadingPage): CallOnlyChatDynamic" />
        }
      >
        <CallOnlyChatDynamic {...props} isCallFinished={isCallFinished} />
      </Suspense>
    )
  }

  if (meetingMode === VacancyMeetingMode.voice_only) {
    callDialogService.strategy = '11labs'

    return (
      <Suspense
        fallback={
          <LoadingPage tag="Suspense fallback (LoadingPage): CallWith11LabsDynamic" />
        }
      >
        <CallWith11LabsDynamic {...props} isCallFinished={isCallFinished} />
      </Suspense>
    )
  }

  callDialogService.strategy = 'heygen'
  return (
    <Suspense
      fallback={
        <LoadingPage tag="Suspense fallback (LoadingPage): CallWithHeygenDynamic" />
      }
    >
      <CallWithHeygenDynamic {...props} isCallFinished={isCallFinished} />
    </Suspense>
  )
}
